<template>
  <page class="stay">
    <header-component
      :is-sticky="true"
      :logo="logoType"
      :content-props="{
        joinPage: true,
        hasLoginLink: true,
        inviteCode,
        loginPath,
        signupPath,
        signupPopupEnabled: true,
        cta: !isUserLoggedIn
          ? {
              copy: cta,
              openSignup: true,
              data: {
                'login-path': loginPath,
                'signup-path': signupPath
              }
            }
          : null
      }"
    />

    <section>
      <hero-section-connect
        :signup-headline="signupHeadline"
        :login-headline="loginHeadline"
        :inviter-name="sanitizedInviterName"
        class="mb-10 l:mb-16"
      />

      <template v-if="!didUserJustLoginOrSignup">
        <what-you-get :inviter-name="sanitizedInviterName" />

        <pricing-card
          :heading="$t('pricingCardHeadline', { inviterName: sanitizedInviterName })"
          :description="$t('pricingSubheadline', { inviterName: sanitizedInviterName })"
        />

        <buttons-section-connect
          class="text-center my-8 mb-16 container"
          personalise
          :signup-headline="signupHeadline"
          :login-headline="loginHeadline"
          :inviter-name="sanitizedInviterName"
          :invite-code="inviteCode"
          :show-signup="!isUserLoggedIn"
        />

        <organisations-category-explorer :inviter-name="sanitizedInviterName" :categories="categoryProps" />

        <buttons-section-connect
          class="text-center mb-16 container"
          personalise
          :signup-headline="signupHeadline"
          :login-headline="loginHeadline"
          :inviter-name="sanitizedInviterName"
          :invite-code="inviteCode"
          :show-signup="!isUserLoggedIn"
        />

        <faq type="b2cMultiUserPlan" />

        <app-reviews :show-trial-end-date="false">
          <template v-slot:cta>
            <buttons-section-connect
              full-width
              class="mt-6 m:mt-8 w-full m:w-72"
              personalise
              :signup-headline="signupHeadline"
              :login-headline="loginHeadline"
              :inviter-name="sanitizedInviterName"
              :invite-code="inviteCode"
              :show-signup="!isUserLoggedIn"
            />
          </template>
        </app-reviews>
      </template>
    </section>
  </page>
</template>

<script>
import Page from "@/shared/page"
import HeaderComponent from "@/features/header/components"
import Faq from "@/features/expendable-faq/expendable-faq"
import { mapGetters } from "vuex"
import { getLastPathParam, getSearchParam } from "@/utils/location"
import HeroSectionConnect from "@/pages/organisations/components/connect/hero-section-connect"
import ButtonsSectionConnect from "@/pages/organisations/components/connect/buttons-section-connect"
import PricingCard from "@/pages/organisations/components/pricing-card"
import AppReviews from "@/features/app-reviews"
import OrganisationsCategoryExplorer from "@/pages/organisations/components/category-explorer"
import globals from "@/utils/globals"
import WhatYouGet from "@/pages/organisations/components/connect/what-you-get"
import sanitizeHtml from "sanitize-html"

export default {
  name: "OrganisationsJoin",
  components: {
    WhatYouGet,
    PricingCard,
    ButtonsSectionConnect,
    HeroSectionConnect,
    Page,
    HeaderComponent,
    Faq,
    AppReviews,
    OrganisationsCategoryExplorer
  },
  props: {
    categoryProps: {
      type: Array,
      default: () => [],
      required: true
    },
    inviterName: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      logoType: null,
      signupHeadline: this.$t("signupHeadline", { inviterName: this.sanitizedInviterName }),
      loginHeadline: this.$t("loginHeadline", { inviterName: this.sanitizedInviterName })
    }
  },
  computed: {
    ...mapGetters(["isUserLoggedIn", "usersEmail"]),
    didUserJustLoginOrSignup() {
      return getSearchParam("fromLoginSignup") === "true"
    },
    sanitizedInviterName() {
      return sanitizeHtml(this.inviterName, { allowedTags: [], allowedAttributes: {} })
    },
    inviteCode() {
      return getLastPathParam()
    },
    joinPathAfterLoginOrSignup() {
      return this.inviteCode ? `/${this.$i18n.locale}/nc/connect/${this.inviteCode}?fromLoginSignup=true` : ""
    },
    signupPath() {
      return `${globals.routes.signup}?last_page_before_login=${encodeURIComponent(this.joinPathAfterLoginOrSignup)}`
    },
    loginPath() {
      return `${globals.routes.login}?last_page_before_login=${encodeURIComponent(this.joinPathAfterLoginOrSignup)}`
    },
    cta() {
      return this.$t("cta", { inviterName: this.sanitizedInviterName })
    }
  },
  mounted() {
    this.getLogoType()
    window.addEventListener("resize", this.getLogoType)
    this.initHeaderSignupTexts()
    return this.$store.dispatch("loadUsersAccounts")
  },
  methods: {
    getLogoType() {
      this.logoType = window && window.innerWidth <= 375 ? "no-text-2" : "full"
    },
    initHeaderSignupTexts() {
      const joinBtn = document.querySelector(".header .js-open-signup-popup")
      if (!joinBtn) return
      joinBtn.addEventListener("click", () => {
        setTimeout(() => {
          const headline = document.querySelector(".auth-popup-form__headline")
          headline.innerHTML = this.signupHeadline
        }, 0)
      })
      const loginBtn = document.querySelector(".header .js-open-login-popup")
      if (!loginBtn) return
      loginBtn.addEventListener("click", () => {
        setTimeout(() => {
          const headline = document.querySelector(".auth-popup-form__headline")
          headline.innerHTML = this.loginHeadline
        }, 0)
      })
    }
  },
  i18n: {
    messages: {
      en: {
        cta: "Join {inviterName}",
        signupHeadline:
          "Create your account to <br/> join {inviterName} <br /> <p class='mt-2 -mb-4 mx-auto' style='width:288px'>Start sharing your favorite aha-moments on Blinkist Premium by creating your account</p>",
        loginHeadline: "Log in to join {inviterName}",
        pricingCardHeadline: "ALREADY PAID BY {inviterName}",
        pricingSubheadline: "This subscription was paid from {inviterName} account."
      },
      de: {
        cta: "Blinkist entdecken",
        signupHeadline:
          "Erstelle deinen Account innerhalb von {inviterName}s Abo <br /> <p class='mt-2 -mb-4'>Mit deinem Account erhältst du vollen Zugang zu unserer Bibliothek und kannst Aha-Momente teilen.</p>",
        loginHeadline: "Melde dich an",
        pricingCardHeadline: "SCHON BEZAHLT VON {inviterName}",
        pricingSubheadline: "Dieses Abo wurde von {inviterName} bezahlt."
      }
    }
  }
}
</script>
